import { isEmpty } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import { inExperiment } from '../../../../utils/experiments';
import { getValidExtras } from '../../../../utils/packagesUtils';
import {
    GOATS_GUEST_SKIP_CHECKOUT_MOBILE_WEB,
    GOATS_GUEST_SKIP_CHECKOUT_WEB,
    GOATS_SKIP_EXTRAS_PAGE_MOBILE_WEB,
    GOATS_SKIP_EXTRAS_PAGE_WEB
} from '../../../../utils/experiments/experimentsEnum';
import { isLoggedInUser } from '../../../../utils/isLoggedInUser';

/**
 * Returns whether the user is eligible for one of the GOATS_SKIP_EXTRAS experiments' experience
 * @returns {boolean}
 */
const inSkipCheckoutExp = () => {
    const { abTests, isMobile } = getContext();

    const inExperimentMobileWeb = isMobile && inExperiment(GOATS_SKIP_EXTRAS_PAGE_MOBILE_WEB, abTests);
    const inExperimentWeb = !isMobile && inExperiment(GOATS_SKIP_EXTRAS_PAGE_WEB, abTests);

    return isLoggedInUser() && (inExperimentWeb || inExperimentMobileWeb);
};

/**
 * Returns whether the user is eligible for one of the GOATS_GUEST_SKIP_CHECKOUT experiments' experience
 * @returns {boolean}
 */
export const inGuestSkipCheckoutExp = () => {
    const { abTests, isMobile } = getContext();

    const inExperimentMobileWeb = isMobile && inExperiment(GOATS_GUEST_SKIP_CHECKOUT_MOBILE_WEB, abTests);
    const inExperimentWeb = !isMobile && inExperiment(GOATS_GUEST_SKIP_CHECKOUT_WEB, abTests);

    return !isLoggedInUser() && (inExperimentWeb || inExperimentMobileWeb);
};

/**
 * Returns whether the login modal should be triggered
 * @param {object} selectedPackageData selected package data (from store)
 * @param {object[]} notIncludedFeatures array of all not included features (from store)
 * @returns {boolean} should trigger the login modal upon before submitting the form
 */
export const shouldTriggerLoginModal = ({ selectedPackageData, notIncludedFeatures }) => {
    if (!selectedPackageData || !notIncludedFeatures) {
        return false;
    }

    return inGuestSkipCheckoutExp() && !packageHasExtras({ selectedPackageData, notIncludedFeatures });
};

/**
 * Returns whether the checkout page should be skipped
 * @param {object} selectedPackageData selected package data (from store)
 * @param {object[]} notIncludedFeatures array of all not included features (from store)
 * @returns {boolean} whether the form should have payment inputs (rather then checkout)
 */
export const shouldSkipCheckout = ({ selectedPackageData, notIncludedFeatures }) => {
    if (!selectedPackageData || !notIncludedFeatures) {
        return false;
    }

    return !packageHasExtras({ selectedPackageData, notIncludedFeatures }) && (inSkipCheckoutExp() || inGuestSkipCheckoutExp());
};

export const shouldSendGuestCheckoutToken = () => !isLoggedInUser() && inGuestSkipCheckoutExp();

/**
 * Returns whether a package has extras
 * @param {object} selectedPackageData selected package data (from store)
 * @param {object[]} notIncludedFeatures array of all not included features (from store)
 * @returns {boolean} true if the package has extras
 */
export const packageHasExtras = ({ selectedPackageData, notIncludedFeatures }) => {
    const { features, customExtras, extraFast, calculators } = selectedPackageData;
    const allFeatures = [ ...features, ...notIncludedFeatures ];
    const validExtras = getValidExtras({ features: allFeatures, customExtras, extraFast });

    return !isEmpty(validExtras) || !isEmpty(calculators);
};

/**
 * @constant {number}
 * @desc Hexadecimal base value
 */
const HEX_BASE = 16;

/**
 * @constant {number}
 * @desc guest checkout token length
 */
const TOKEN_LENGTH = 12;

/**
 * Generates a 12 random hex digits as string
 * @returns {string} generated token
 */
export const generateGuestCheckoutToken = () =>
    [...Array(TOKEN_LENGTH)].map(() => (Math.floor(Math.random() * HEX_BASE)).toString(HEX_BASE)).join('');
